import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PlayCircle, Coins, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { usePageMeta } from "@/lib/seo";

const fmt = (d) => new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
const thumb = (v) => (v.youtube_id ? `https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg` : v.thumbnail);

export const Shell = ({ children, testId }) => {
  const { user } = useAuth();
  return (
    <div className="min-h-screen noise-bg" data-testid={testId}>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-pink-100">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <Logo to="/" />
          <Link to={user ? "/feed" : "/registrieren"} data-testid="public-video-header-cta" className="px-4 h-9 inline-flex items-center rounded-full pink-gradient text-white text-sm font-bold">{user ? "Zur App" : "Kostenlos mitmachen"}</Link>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-8 sm:py-12">{children}</main>
    </div>
  );
};

export const Cta = ({ v }) => {
  const { user } = useAuth();
  return (
    <div className="rounded-3xl p-6 pink-gradient text-white shadow-xl shadow-rose-200 space-y-3" data-testid="public-video-cta">
      <p className="font-display text-xl font-extrabold">Schau das Video in der Community und sammle {v?.points ? `${v.points} ` : ""}Punkte!</p>
      <p className="text-sm text-pink-50">Punkte für Videos, Login und Freunde – und dann gegen Geschenke von itzcouple eintauschen.</p>
      <Link to={user ? "/feed" : "/registrieren"} data-testid="public-video-cta-btn" className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-white text-rose-600 font-extrabold text-sm hover:scale-[1.03] transition-transform">
        {user ? "In der App ansehen" : "Kostenlos registrieren"} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
};

export function PublicVideoList() {
  const [list, setList] = useState(null);
  usePageMeta({ title: "Alle Videos von itzcouple", description: "Alle YouTube-Videos des Paares itzcouple auf einen Blick – in der itzPlay Community anschauen und Punkte für Geschenke sammeln." });
  useEffect(() => { api.get("/public/videos").then(({ data }) => setList(data)).catch(() => setList([])); }, []);
  return (
    <Shell testId="public-video-list">
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Videos von <span className="text-rose-500">itzcouple</span></h1>
      <p className="mt-3 text-slate-600 max-w-2xl">Alle unsere YouTube-Videos. In der Community bekommst du für jedes angeschaute Video Punkte, die du gegen Geschenke eintauschen kannst.</p>
      {!list ? <Loader2 className="mt-10 w-6 h-6 animate-spin text-rose-500" /> : (
        <ul className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((v, i) => (
            <li key={v.id} className="fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <Link to={`/video/${v.id}`} data-testid={`public-video-${v.id}`} className="group block bg-white rounded-3xl overflow-hidden border border-pink-100 hover:-translate-y-1 transition-transform shadow-sm">
                <div className="relative aspect-video bg-rose-50">
                  <img src={thumb(v)} alt={`${v.title} – Video von itzcouple`} loading="lazy" className="w-full h-full object-cover" />
                  <PlayCircle className="absolute inset-0 m-auto w-14 h-14 text-white drop-shadow-lg group-hover:scale-110 transition-transform" />
                </div>
                <div className="p-4"><h2 className="font-bold leading-snug line-clamp-2">{v.title}</h2><p className="text-xs text-slate-400 mt-1">{fmt(v.created_at)} · +{v.points} Punkte</p></div>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-10"><Cta /></div>
    </Shell>
  );
}

const useVideoJsonLd = (v) => useEffect(() => {
  if (!v) return undefined;
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.text = JSON.stringify({ "@context": "https://schema.org", "@type": "VideoObject", name: v.title, description: v.description || `${v.title} – ein Video von itzcouple`, thumbnailUrl: [thumb(v)], uploadDate: v.created_at, ...(v.youtube_id ? { embedUrl: `https://www.youtube.com/embed/${v.youtube_id}` } : {}), publisher: { "@type": "Organization", name: "itzcouple" } });
  document.head.appendChild(el);
  return () => el.remove();
}, [v]);

export function PublicVideo() {
  const { id } = useParams();
  const [v, setV] = useState(undefined);
  useEffect(() => { api.get(`/public/videos/${id}`).then(({ data }) => setV(data)).catch(() => setV(null)); }, [id]);
  usePageMeta({ title: v ? v.title : "Video", description: v ? (v.description || `${v.title} – das neue Video von itzcouple. In der itzPlay Community ansehen und Punkte sammeln.`).slice(0, 160) : undefined, noindex: v === null });
  useVideoJsonLd(v);
  return (
    <Shell testId="public-video-page">
      <Link to="/video" data-testid="public-video-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-rose-600"><ArrowLeft className="w-4 h-4" /> Alle Videos</Link>
      {v === undefined && <Loader2 className="mt-10 w-6 h-6 animate-spin text-rose-500" />}
      {v === null && <p className="mt-10 text-slate-500" data-testid="public-video-missing">Dieses Video gibt es nicht mehr.</p>}
      {v && (
        <article className="mt-5 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-start">
          <div className="relative aspect-video rounded-3xl overflow-hidden bg-rose-50 shadow-xl shadow-rose-100">
            <img src={thumb(v)} alt={`${v.title} – Video von itzcouple`} className="w-full h-full object-cover" data-testid="public-video-thumb" />
            <PlayCircle className="absolute inset-0 m-auto w-20 h-20 text-white/90 drop-shadow-lg" />
          </div>
          <div className="space-y-5">
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight" data-testid="public-video-title">{v.title}</h1>
              <p className="mt-2 text-sm text-slate-400">Video von itzcouple · {fmt(v.created_at)}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold" data-testid="public-video-points"><Coins className="w-4 h-4" /> +{v.points} Punkte in der Community</span>
            {v.description && <p className="text-slate-600 whitespace-pre-line" data-testid="public-video-desc">{v.description}</p>}
            <Cta v={v} />
          </div>
        </article>
      )}
    </Shell>
  );
}
